import axios from '../baseClient';

import {
    GetStrategiesResponse,
    GetStrategyResponse,
    GetStrategyCombinationsResponse,
    GetStrategyOptimisationsResponse,
    GetOptimizationTypesResponse,
    GetStrategyIndicatorInstancesResponse,
    GetStrategyInstrumentResponse,
    GetStrategyChangesResponse,
    GetStrategyExecutionsResponse,
    GetStrategyNodeTemplatesResponse,
    GetStrategyTemplateResponse,
    GetTemplatesResponse,
    CreateStrategyRequest,
    StrategyResponse,
} from './types';

class AIStrategyBuilderService {
    private static domain = '/api/aistrategybuilder';

    getTemplates() {
        return axios.get<GetTemplatesResponse>(`${AIStrategyBuilderService.domain}/frontend/strategy/templates`);
    }

    getStrategyTemplate(uuid: string) {
        return axios.get<GetStrategyTemplateResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/templates/${encodeURIComponent(uuid)}`
        );
    }

    getStrategyNodeTemplates() {
        return axios.get<GetStrategyNodeTemplatesResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/node-templates`
        );
    }

    getStrategyIndicatorInstances() {
        return axios.get<GetStrategyIndicatorInstancesResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/indicator-instances`
        );
    }

    getStrategyCombinations() {
        return axios.get<GetStrategyCombinationsResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/combinations`
        );
    }

    getStrategyOptimisations() {
        return axios.get<GetStrategyOptimisationsResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/optimisations`
        );
    }

    getOptimizationTypes() {
        return axios.get<GetOptimizationTypesResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/optimization-types`
        );
    }

    getStrategyInstrument(instrumentCode: string) {
        return axios.get<GetStrategyInstrumentResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategy/instrument`,
            { params: { instrument_code: instrumentCode } }
        );
    }

    getStrategies(params?: {
        page?: number;
        size?: number;
        // "<field>,<asc|desc>"; allowed fields: id, created (default desc), updated, status,
        // instrument_code, instrument_period. Unknown fields silently fall back to created.
        sort?: string;
        instrument_code?: string;
        instrument_period?: string;
        status?: string;
        side?: 'buy' | 'sell';
        // Return only archived strategies (takes precedence over includeArchived).
        showOnlyArchived?: boolean;
        // Include archived strategies alongside active ones.
        includeArchived?: boolean;
        // Include each row's full `input` JSON (opt-in; ~3x the payload). The list no longer
        // needs it — side/TP/SL/indicators come from frontend_json instead.
        returnInputJSON?: boolean;
        // Include each row's `frontend_json` blob — the only source of side, TP/SL and the
        // indicator names on list items.
        returnFrontendJSON?: boolean;
        returnBackendJSON?: boolean;
    }) {
        return axios.get<GetStrategiesResponse>(`${AIStrategyBuilderService.domain}/frontend/strategies`, { params });
    }

    // Every heavy blob on the detail endpoint is opt-in (backend change 2026-07-26): without
    // these flags the response carries only the scalar columns. `returnOutputJSON` covers the
    // engine's output (the `response` field).
    getStrategy(
        id: string,
        params?: {
            returnInputJSON?: boolean;
            returnOutputJSON?: boolean;
            returnFrontendJSON?: boolean;
            returnBackendJSON?: boolean;
        }
    ) {
        return axios.get<GetStrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}`,
            { params }
        );
    }

    getStrategyChanges(id: string, params?: { page?: number; size?: number; sort?: string }) {
        return axios.get<GetStrategyChangesResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/changes`,
            { params }
        );
    }

    getStrategyExecutions(id: string, params?: { page?: number; size?: number; sort?: string }) {
        return axios.get<GetStrategyExecutionsResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/executions`,
            { params }
        );
    }

    createStrategy(payload: CreateStrategyRequest) {
        return axios.post<StrategyResponse>(`${AIStrategyBuilderService.domain}/frontend/strategies`, payload);
    }

    enableStrategy(id: string) {
        return axios.post<StrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/enable`
        );
    }

    disableStrategy(id: string) {
        return axios.post<StrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/disable`
        );
    }

    // The user viewed the backtesting result and tapped "Next": done -> seen.
    markSeen(id: string) {
        return axios.post<StrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/seen`
        );
    }

    // The user dismissed the result; it is not activated for trading: done|seen -> discarded.
    discardStrategy(id: string) {
        return axios.post<StrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/discard`
        );
    }

    // Hide a finished strategy from the default list. Archivable statuses per the lifecycle
    // spec: done, seen, failed, disabled, discarded.
    // ASSUMPTION (docs/ai-strategy/00-overview.md #3): the endpoint mirrors the other lifecycle
    // transitions — verify the real path/response with the backend before wiring UI to it.
    archiveStrategy(id: string) {
        return axios.post<StrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/archive`
        );
    }

    // Puts an archived strategy back into the default listing.
    unarchiveStrategy(id: string) {
        return axios.post<StrategyResponse>(
            `${AIStrategyBuilderService.domain}/frontend/strategies/${encodeURIComponent(id)}/unarchive`
        );
    }
}

export default new AIStrategyBuilderService();
